import React, { useContext, useEffect, useRef, useState } from 'react';
import L from 'leaflet'; // Import Leaflet library
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
import translations from '../../components/common/Translations';
import AppContext from '../../context/AppContext';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const MapModal = ({ isOpen, onClose, onLocationSelect, initialLat, initialLon }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const [currentLat, setCurrentLat] = useState(initialLat);
  const [currentLon, setCurrentLon] = useState(initialLon);
  const {langMode} = useContext(AppContext)
  useEffect(() => {
    // Coordinates for the center of Oman (approximately)
    const OMAN_LAT = 21.0000;
    const OMAN_LON = 57.0000;
    const OMAN_ZOOM = 7; // Changed from 20 to 6 for a more appropriate country-level view

    if (isOpen && !mapInstance.current) {
      // Define base layers
      const satelliteLayer = L.tileLayer('https://mt.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
        attribution: '© <a href="https://www.google.com/maps">Google Maps</a>'
      });

      const openStreetMapLayer = L.tileLayer('https://tile.openstreetmap.de/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      });

      mapInstance.current = L.map(mapRef.current, {
        center: [initialLat || OMAN_LAT, initialLon || OMAN_LON],
        zoom: initialLat && initialLon ? 15 : OMAN_ZOOM,
        layers: [satelliteLayer] // Set satellite as the default base layer
      });

      const baseLayers = {
        "Satellite View": satelliteLayer,
        "OpenStreetMap": openStreetMapLayer
      };

      L.control.layers(baseLayers).addTo(mapInstance.current);

      mapInstance.current.on('click', (e) => {
        const { lat, lng } = e.latlng;
        setCurrentLat(lat);
        setCurrentLon(lng);
        onLocationSelect(lat, lng);

        // Update marker position
        if (markerInstance.current) {
          markerInstance.current.setLatLng([lat, lng]);
        } else {
          markerInstance.current = L.marker([lat, lng]).addTo(mapInstance.current);
        }
      });
    }

    if (isOpen && mapInstance.current) {
      mapInstance.current.invalidateSize();
      if (initialLat && initialLon) {
        if (markerInstance.current) {
          markerInstance.current.setLatLng([initialLat, initialLon]);
        } else {
          markerInstance.current = L.marker([initialLat, initialLon]).addTo(mapInstance.current);
        }
        mapInstance.current.setView([initialLat, initialLon], 7); // Zoom in on initial location (15 is usually good for a specific point)
      } else {
        // If no initial coords, clear any existing marker and reset view to Oman
        if (markerInstance.current) {
          mapInstance.current.removeLayer(markerInstance.current);
          markerInstance.current = null;
        }
        mapInstance.current.setView([OMAN_LAT, OMAN_LON], OMAN_ZOOM); // Default to Oman
      }
    }
    return () => {
      if (!isOpen && mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        markerInstance.current = null;
      }
    };
  }, [isOpen, initialLat, initialLon, onLocationSelect]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[1px]">
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-2xl max-w-3xl w-full h-3/4 flex flex-col">
        <div className='flex justify-between items-center pb-3 mb-1 border-b border-slate-100'>
          <h2 className="text-base font-semibold text-slate-800">{translations[langMode].selectlocationmap}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <div ref={mapRef} className="flex-grow w-full" style={{ height: 'calc(100% - 60px)' }}></div> {/* Adjusted height */}
      </div>
    </div>
  );
};

export default MapModal;